import { vmWareProducts } from "./constants";
import { cleanMarkdownBeforeDB } from "./sharedUtils";
import { formatDate } from "./utils";

export function createEmptyEvent() {
  return {
    partnerId: "",
    Partner: {},
    title: "",
    color: "",
    type: "",
    description: "",
    tagline: "",
    startDate: "",
    endDate: "",
    startTime: "",
    endTime: "",
    location: "",
    link: "",
    // guests: {},
    notes: "",
    image: "",
  };
}

export function hasInvalidFields(event) {
  const requiredEventFields = {
    title: "Title",
    type: "Type",
    tagline: "Tagline",
    description: "description",
  };

  const invalidEventFields = Object.keys(requiredEventFields).filter(
    (field) => !event[field] || event[field].length === 0
  );

  const allInvalidFields = invalidEventFields.map(
    (field) => requiredEventFields[field]
  );
  const status = allInvalidFields.length > 0;

  const message = status
    ? `Please complete all required fields: ${allInvalidFields
        .join(", ")
        .concat(".")}`
    : "";

  return [status, message];
}

export function parseCreateEvent(event, isAdmin) {
  const partner = isAdmin
    ? undefined
    : event.partnerId
      ? { connect: { id: parseNumberField(event.partnerId) } }
      : undefined;

  const provider = isAdmin
    ? undefined
    : event.providerId
      ? { connect: { id: parseNumberField(event.providerId) } }
      : undefined;

  return {
    title: parseStringField(event.title),
    color: parseStringField(event.color),
    EventTypes: {
      connect: {
        id: parseNumberField(event.type),
      },
    },
    tagline: parseStringField(event.tagline),
    timezone: parseStringField(event.timezone),
    description: parseStringField(cleanMarkdownBeforeDB(event.description)),
    startDate: parseStringField(event.startDate),
    endDate: parseStringField(event.endDate),
    products: event.products,
    cloudServices: event.providers,
    location: parseStringField(event.location),
    link: parseStringField(event.link),
    notes: parseStringField(event.notes),
    image: event.image,
    Partner: partner,
    LocatorCompany: provider,
    contentStatus: isAdmin ? "ACCEPTED" : "REQUIRED_REVIEW",
    allDayEvent: event.allDayEvent,
    region: event.regionId
      ? {
          connect: {
            id: parseNumberField(event.regionId.id),
          },
        }
      : undefined,
  };
}

export function parseUpdateEvent(event, isAdmin) {
  const parsedEvent = {};

  const frontendToBackEndConv = {
    type: "EventTypes",
    regionId: "region",
  };

  const mapFieldsToParse = {
    title: parseStringField,
    color: parseStringField,
    type: parseEventType,
    tagline: parseStringField,
    timezone: parseStringField,
    description: (field) => parseStringField(cleanMarkdownBeforeDB(field)),
    startDate: parseStringField,
    endDate: parseStringField,
    products: parseProducts,
    location: parseStringField,
    link: parseStringField,
    // guests: parseUnique,
    notes: (field) => parseStringField(cleanMarkdownBeforeDB(field)),
    image: parseUnique,
    id: parsePartnerIdField,
    partnerId: parseNumberField,
    regionId: parseEventRegion,
    providerId: parseNumberField,
    providers: parseProviders,
  };

  for (const prop in event) {
    parsedEvent[frontendToBackEndConv[prop] || prop] = mapFieldsToParse[prop]
      ? mapFieldsToParse[prop](event[prop])
      : event[prop];
  }

  parsedEvent["contentStatus"] = isAdmin ? "ACCEPTED" : "REQUIRED_REVIEW";

  if (parsedEvent["providers"]) {
    parsedEvent["cloudServices"] = { ...parsedEvent["providers"] };

    delete parsedEvent["providers"];
  }

  return parsedEvent;
}

const parseNumberField = (field) => field && Number(field);
const parsePartnerIdField = (field) =>
  field && { Partner: { connect: { id: Number(field) } } };
const parseStringField = (field) => (field && String(field).trim()) || "";
const parseDateField = (field) =>
  (field && new Date(String(field).trim())) || "";
const parseUnique = (field) => field;
const parseProducts = (field) => ({
  set: field?.map(({ id }) => ({ id })),
});
const parseProviders = (field) => ({
  set: field?.map(({ value }) => ({ id: value })),
});
const parseEventType = (field) => ({
  connect: {
    id: parseNumberField(field.id),
  },
});
const parseEventRegion = (field) =>
  field.id
    ? {
        connect: {
          id: parseNumberField(field.id),
        },
      }
    : undefined;

export const colors = [
  { id: 1, label: "#EB5757", value: "#EB5757", color: "#EB5757" },
  { id: 2, label: "#FFAFA6", value: "#FFAFA6", color: "#FFAFA6" },
  { id: 3, label: "#CF976F", value: "#CF976F", color: "#CF976F" },
  { id: 4, label: "#FFCB33", value: "#FFCB33", color: "#FFCB33" },
  { id: 5, label: "#6FCF97", value: "#6FCF97", color: "#6FCF97" },
  { id: 6, label: "#397E49", value: "#397E49", color: "#397E49" },
  { id: 7, label: "#6FCFCF", value: "#6FCFCF", color: "#6FCFCF" },
  { id: 8, label: "#4252AF", value: "#4252AF", color: "#4252AF" },
  { id: 9, label: "#996FCF", value: "#996FCF", color: "#996FCF" },
  { id: 10, label: "#CF6FC5", value: "#CF6FC5", color: "#CF6FC5" },
  { id: 11, label: "#716FCF", value: "#716FCF", color: "#716FCF" },
  { id: 12, label: "#616161", value: "#616161", color: "#616161" },
];

export const getPublicEventTimeAndDate = (selectedEvent, nativeNavigator) => {
  const startDate = selectedEvent?.startDate.slice(0, 11);
  const startTime = selectedEvent?.startTime.slice(11, 19);
  const addHourStartDate = `${startDate.concat(startTime)}`;

  const hourFormat = Intl.DateTimeFormat(nativeNavigator?.language, {
    hour: "numeric",
  }).resolvedOptions().hourCycle;

  const time = new Intl.DateTimeFormat(nativeNavigator?.language, {
    hour: "numeric",
    minute: "numeric",
    hour12: hourFormat === "h12" ? true : false,
  });

  const sd = selectedEvent?.allDayEvent
    ? new Date(
        selectedEvent?.startDate.slice(0, selectedEvent?.startDate.length - 1)
      )
    : new Date(addHourStartDate);

  const hourStart = time.format(sd);

  const splitStartDate = selectedEvent?.allDayEvent
    ? formatDate(new Date(addHourStartDate).toISOString().split("T")[0])
    : new Date(addHourStartDate).toLocaleDateString("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });

  const endDate = selectedEvent?.endDate.slice(0, 11);
  const endTime = selectedEvent?.endTime.slice(11, 19);
  const addHourEndDate = `${endDate.concat(endTime)}`;

  const ed = new Date(addHourEndDate);
  const hourEnd = time.format(ed);

  const splitEndDate = selectedEvent?.allDayEvent
    ? formatDate(new Date(addHourEndDate).toISOString().split("T")[0])
    : new Date(addHourEndDate).toLocaleDateString("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });

  return {
    splitStartDate,
    splitEndDate,
    hourStart,
    hourEnd,
    addHourStartDate,
    addHourEndDate,
    startDate,
    startTime,
    sd,
    ed,
    endDate,
    endTime,
    time,
    hourFormat,
  };
};

export const VMwareProductsFiltring = (events) => {
  const onlyLevel2isRelevant = (level2) => vmWareProducts.includes(level2);

  return (
    events?.map((event) => {
      return {
        ...event,
        products: event?.products?.reduce((uniqueProducts, product) => {
          const level3Value = onlyLevel2isRelevant(product.level2)
            ? product.level2
            : product.level3;
          if (!uniqueProducts.find((up) => up.level3 === level3Value)) {
            uniqueProducts.push({
              ...product,
              level3: level3Value,
            });
          }
          return uniqueProducts;
        }, []),
      };
    }) || []
  );
};
